import * as React from 'react';
import { useMemo } from 'react';
import * as R from 'ramda';
import ProjectLayout from '../ProjectLayout';
import SurveyHeader from '../survey/SurveyHeader';
import { onMobileDevice } from '../../globalStyle/mediaQueries';
import { useMsgText } from '../../localization/hooks';
import { Str } from '../../localization/translationKeys';
import { useAppState } from '../../core/redux/hooks';
import { css } from '@emotion/core';
import LocalDiv from '../../localization/LocalDiv';
import { COLORS } from '../../globalStyle/colors';
import Msg from '../../localization/Msg';
import Spacer from '../../layout/Spacer';
import { faExclamationTriangle, faCheck } from '@fortawesome/free-solid-svg-icons';
import { FLEX_ADAPTIVE } from '../../layout/flexBox';
import { ProjStr } from '../messages/ProjStr';
import ProjMsg from '../messages/ProjMsg';
import { useDispatch } from 'react-redux';
import { useMount } from '../../core/hooks/useMount';
import { getSelectedQuestionsAction } from './api/getSelectedQuestionsAction';
import { getCompetenciesAndQuestionsAction } from './api/getCompetenciesAndQuestionsAction';
import { addQuestionAction } from './api/addQuestionAction';
import { addQuestionsForCompetencyAction } from './api/addQuestionsForCompetencyAction';
import { deleteQuestionAction } from './api/deleteQuestionAction';
import { deleteQuestionsForCompetencyAction } from './api/deleteQuestionsForCompetencyAction';
import { applyPendingChangesAction } from './api/applyPendingChangesAction';
import CreateSurveyMessageBar from './CreateSurveyMessageBar';
import CreateSurveyAdded from './CreateSurveyAdded';
import CreateSurveyRequired from './CreateSurveyRequired';
import CreateSurveyAddModal from './CreateSurveyAddModal';
import { useToggle } from '../../core/hooks/useToggle';
import { getDevPlanItemsAction } from '../devPlan/api/getDevPlanItemsAction';
import { unsetCreatedSurveyAction } from './api/unsetCreatedSurveyAction';
import { useProjIds } from '../../navigation/hooks';


const styles = {
  header:    css`
    margin: auto 25px 40px;
    ${onMobileDevice} {
      margin: auto 10px 35px;
    }
  `,
  content:   css`
    color: ${COLORS.DARK_GREY};
    margin-top: 75px;
  `,
  errorMsg:  css`
    color: ${COLORS.RED};
    background-color: transparent;
  `,
  infoMsg:   css`
    color: ${COLORS.GREEN};
    background-color: transparent;
  `,
  finishMsg: css`
    color: ${COLORS.DARK_GREY};
    background-color: transparent;
  `,
  added:     css`
    min-width: 300px;
  `,
  required:  css`
    min-width: 300px;
  `,
};

type Props = {
  className?: string;
};


/**
 * CreateSurvey: a component that allows the user to create a feedback survey
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurvey: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const project = useAppState(state => state.projectSummary);

  const projIds       = useProjIds();
  const projectId     = projIds.projId;
  const participantId = projIds.participantId;

  const devPlanProjectId = project?.devPlanProjectId || projectId;

  const pageTitle = useMsgText(Str.CreateSurvey);

  const addByCompetency = project?.followupSurveyAddByCompetency!;

  useMount(() => { dispatch(getSelectedQuestionsAction(projectId, participantId)); });

  const questions = useAppState(state => state.selectedQuestions?.[`${projectId}`] ?? []);

  const { addedQuestions, requiredQuestions, addedQuestionsForCount } = useMemo(
    () => ({
      addedQuestions:         R.filter(item => !item.required, questions),
      requiredQuestions:      R.filter(item => item.required, questions),
      addedQuestionsForCount: R.filter(item => !item.required && item.competencyId > 0, questions),
    }),
    [questions],
  );

  const count = useMemo(
    () => addByCompetency
      ? R.uniqBy(({ competencyId }) => competencyId, addedQuestionsForCount).length || 0
      : addedQuestionsForCount.length || 0,
    [addByCompetency, addedQuestionsForCount],
  );

  const itemMin = project?.followupSurveyMinimumQuestion ?? 0;
  const itemMax = project?.followupSurveyMaximumQuestion ?? 0;

  const surveyCreated = project?.followupSurveyCreated ?? false;
  const raterInvited = project?.raterInvited ?? false;
  const selfSurveyStarted = (project?.selfSurveyStep && project?.selfSurveyAmountComplete > 0) ?? false;

  const showMaxErr  = itemMax > 0 && count > itemMax;
  const showReady   = !showMaxErr && count > 0 && count >= itemMin;
  const showCreated = surveyCreated && !raterInvited && !selfSurveyStarted;
  const showFinal   = surveyCreated && (raterInvited || selfSurveyStarted);
  const showAdd     = !(showReady || showCreated || showFinal);

  const iconColor = (
    showMaxErr
      ? COLORS.RED
      : showCreated || showFinal
      ? COLORS.BLUE
      : COLORS.GREEN
  );

  const iconImage = (showCreated || showFinal ? faCheck : faExclamationTriangle);

  const addProjMsgStr = (
    project?.followupSurveyMaximumQuestion !== 0 && project?.followupSurveyMinimumQuestion !== 0
      ? ProjStr.CreateSurveyMinMaxMsg
      : project?.followupSurveyMaximumQuestion !== 0
      ? ProjStr.CreateSurveyMaxMsg
      : project?.followupSurveyMinimumQuestion !== 0
        ? ProjStr.CreateSurveyMinMsg
        : ProjStr.CreateSurveyMsg
  );

  const message = (
    showMaxErr
      ? <ProjMsg
        str={ProjStr.CreateSurveyMaxErrMsg}
        curlyTokens={{ 'number': project?.followupSurveyMaximumQuestion }}
        css={styles.errorMsg}
      />
      : showFinal
      ? <Msg str={Str.SurveyFinal} css={styles.finishMsg} />
      : showCreated
        ? <Msg str={Str.SurveyCreated} css={styles.finishMsg} />
        : showReady
          ? <ProjMsg str={ProjStr.CreateSurveyReadyMsg} css={styles.infoMsg} />
          : showAdd && (<ProjMsg str={addProjMsgStr} css={styles.infoMsg} />)
  );

  useMount(() => { dispatch(getCompetenciesAndQuestionsAction(projectId)); });

  const allQuestions = useAppState(state => (state.competenciesAndQuestions?.[`${projectId}`] || []));

  const addQuestion = React.useCallback(
    (questionId: number) => dispatch(addQuestionAction(projectId, participantId, questionId)),
    [dispatch, projectId, participantId],
  );

  const addQuestionsForCompetency = React.useCallback(
    (competencyId: number) => dispatch(addQuestionsForCompetencyAction(projectId, participantId, competencyId)),
    [dispatch, projectId, participantId],
  );
  const deleteQuestion            = React.useCallback(
    (questionId: number) => dispatch(deleteQuestionAction(projectId, participantId, questionId)),
    [dispatch, projectId, participantId],
  );

  const deleteQuestionsForCompetency = React.useCallback(
    (competencyId: number) =>
      dispatch(deleteQuestionsForCompetencyAction(projectId, participantId, competencyId)),
    [dispatch, projectId, participantId],
  );

  const applyPendingChanges = React.useCallback(
    () => dispatch(applyPendingChangesAction(projectId, participantId)),
    [dispatch, projectId, participantId],
  );

  const unsetCreated = React.useCallback(
    () => dispatch(unsetCreatedSurveyAction(projectId, participantId)),
    [dispatch, projectId, participantId],
  );

  const [showingAddModal, toggleAddModal] = useToggle();

  useMount(() => { dispatch(getDevPlanItemsAction(devPlanProjectId, participantId)); });
  const devItems = useAppState(state => state.devPlan?.items ?? []);


  return (
    <ProjectLayout className={props.className}>
      <SurveyHeader title={`/ ${pageTitle}`} deadline={project?.surveyDeadline} css={styles.header} />
      <LocalDiv css={styles.content}>
        <CreateSurveyMessageBar
          show={showMaxErr || showAdd || showReady || showCreated || showFinal || false}
          iconImage={iconImage}
          iconColor={iconColor}
          message={message}
        />
        <LocalDiv css={FLEX_ADAPTIVE}>
          <CreateSurveyAdded
            addDisabled={showFinal}
            count={count}
            createDisabled={!(showReady || showCreated) || showFinal}
            createdMode={showCreated || showFinal}
            addedQuestions={addedQuestions}
            deleteQuestion={deleteQuestion}
            deleteQuestionsForCompetency={deleteQuestionsForCompetency}
            applyPendingChanges={applyPendingChanges}
            addByCompetency={addByCompetency}
            toggleAddModal={toggleAddModal}
            devItems={devItems}
            unsetCreated={unsetCreated}
            css={styles.added}
            fullWidth={requiredQuestions.length === 0}
          />
          {
            requiredQuestions.length > 0 && (
              <>
                <Spacer sizePx={20} />
                <CreateSurveyRequired
                  requiredQuestions={requiredQuestions}
                  devItems={devItems}
                  addByCompetency={addByCompetency}
                  deleteQuestion={deleteQuestion}
                  deleteQuestionsForCompetency={deleteQuestionsForCompetency}
                  css={styles.required}
                />
              </>
            )
          }
        </LocalDiv>
      </LocalDiv>
      {
        showingAddModal && (
          <CreateSurveyAddModal
            dismiss={toggleAddModal}
            modalTitle={ProjStr.AddToSurvey}
            allQuestions={allQuestions}
            selectedQuestions={questions}
            devItems={devItems}
            addByCompetency={addByCompetency}
            addQuestion={addQuestion}
            addQuestionsForCompetency={addQuestionsForCompetency}
          />
        )
      }
    </ProjectLayout>
  );
};

export default CreateSurvey;
